import {FC, useCallback, useContext, useEffect, useState,} from "react";
import {useTranslation} from "react-i18next";
import {MessageBar, MessageBarButton, MessageBarType, PrimaryButton,} from "@fluentui/react";
import Cookies from "js-cookie";
import {useBoolean, usePrevious} from "@fluentui/react-hooks";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import TopBar from "../components/TopBar";
import {LeftNavigation} from "../components/LeftNavigation";
import {NotLoggedInGuard} from "../guards/NotLoggedInGuard";
import {NavigationPanelContext} from "../state/State";
import {AuthContext} from "../contexts/AuthContext";
import {TitleAndContentLayout} from "../components/TitleAndContentLayout";
import {AppRoute} from "../AppRoutes";
import {useLocalStorage} from "../helpers/useLocalStorage";
import {LanguageContext} from "../contexts/LanguageContext";
import {TeamViewerMessageBar} from "../components/TeamViewerMessageBar";
import {CurrentSupportTicketContextEntry} from "../contexts/CurrentSupportTicketContext";
import {OnixPhoneUserTaskListener} from "../components/OnixPhoneUserTaskListener";
import {AccountEmailWarning} from "../components/AccountEmailWarning";

const AdminLayout: FC = () => {
    const history = useNavigate();
    const {search, pathname} = useLocation();
    const location = useLocation();
    const {IsDeleted} = useContext(AuthContext);
    const [NavPanelVisible, SetNavPanelVisible] = useState(false);
    const ToggleNavPanelVisible = () => SetNavPanelVisible(!NavPanelVisible);
    const HideNavPanel = () => SetNavPanelVisible(false);
    const ShowNavPanel = () => SetNavPanelVisible(true);
    const Navigate = (url: string, openInNewTab: boolean | undefined) => {
        const isOutsideLink = url.indexOf("http") != -1;
        if (
            isOutsideLink ||
            url.indexOf(AppRoute.HANGFIRE_DASHBOARD) > -1 ||
            url.indexOf(AppRoute.SWAGGER) > -1 ||
            url.indexOf(AppRoute.GRAPHQL) > -1 ||
            url.startsWith(AppRoute.WEB_APP)
        ) {
            (window as any).location = url;
        } else {
            history(url);
            HideNavPanel();
        }
    };
    const {t} = useTranslation();
    const [comingFromUser] = useLocalStorage("coming_from_user", undefined);
    const [comingFromUserName] = useLocalStorage(
        "coming_from_user_name",
        undefined,
    );
    const [thereIsSomethingNew, setThereIsSomethingNew] = useState<boolean>();
    const {UserInfo} = useContext(AuthContext);
    const {moment, serverDateToLocalDate} = useContext(LanguageContext);

    const prev = usePrevious(search);

    const [refreshing, {setTrue: setRefreshing, setFalse: setNotRefreshing}] =
        useBoolean(false);

    useEffect(() => {
        if (prev != search) {
            setRefreshing();
            setImmediate(setNotRefreshing);
        }
    }, [prev, search]);

    useEffect(() => {
        checkIfUpdated();
        window.addEventListener("storage", checkIfUpdated, false);
    }, []);

    const checkIfUpdated = useCallback(() => {
        const updated = localStorage.getItem("updated");
        if (updated) {
            setThereIsSomethingNew(true);
        }
    }, []);

    useEffect(() => {
        let loc = document.location.pathname.replace("/admin", "");
        if (loc == "") {
            loc = "/";
        }
    }, []);

    const makePath = useCallback(() => {
        let ret = "";

        const path = pathname.split("/");

        let upto = "";
        let l = 0;

        for (const p of path) {
            upto += "/" + p;
            l += 1;
            if (p != "") {
                if (p == "edit" && path.length == l) {
                } else {
                    const trans = t(p);
                    ret += " - " + trans;
                }
            }
        }

        return ret;
    }, [pathname, t]);

    useEffect(() => {
        document.title = "Onix" + makePath();
    }, [location, makePath]);

    useEffect(() => {
        document.title = "Onix" + makePath();
    }, [makePath]);

    const backToComingFromUser = useCallback(() => {
        if (comingFromUser) {
            localStorage.clear();
            Cookies.remove("access_token");
            Cookies.set("access_token", comingFromUser);
            localStorage.setItem("auth_token", comingFromUser!);
            (window as any).location.href = "/dashboard";
        }
    }, [comingFromUser]);

    return (
        <NotLoggedInGuard>
            <TitleAndContentLayout
                errorBandOnly={true}
                error={IsDeleted ? t("this_user_is_deactivated") : undefined}
            />
            <NavigationPanelContext.Provider
                value={{
                    HideNavPanel,
                    NavPanelVisible,
                    ShowNavPanel,
                    ToggleNavPanelVisible,
                    Navigate,
                }}
            >
                <CurrentSupportTicketContextEntry>
                    <LeftNavigation/>
                    <TopBar/>
                    <OnixPhoneUserTaskListener/>
                    <div style={{paddingTop: 40, zIndex: 0}}>
                        {UserInfo?.user && <AccountEmailWarning email={UserInfo.user.email} emailConfirmed={UserInfo.user.emailConfirmed}/>}
                        {refreshing ? null : <Outlet/>}
                    </div>
                    {thereIsSomethingNew && false ? (
                        <MessageBar
                            styles={{root: {position: "absolute", bottom: 0}}}
                            messageBarType={MessageBarType.warning}
                            actions={
                                <MessageBarButton
                                    text={t("refresh")}
                                    onClick={() => window.location.reload()}
                                />
                            }
                        >
                            {t("there-are-some-updates-a-refresh-is-required")}
                        </MessageBar>
                    ) : (
                        <TeamViewerMessageBar/>
                    )}
                </CurrentSupportTicketContextEntry>
            </NavigationPanelContext.Provider>
            {comingFromUser ? (
                <PrimaryButton
                    onClick={backToComingFromUser}
                    style={{position: "absolute", right: 20, bottom: 20}}
                    iconProps={{
                        iconName: "ChevronLeft",
                    }}
                >
                    Nazad na glavnog korisnika ({comingFromUserName})
                </PrimaryButton>
            ) : null}
        </NotLoggedInGuard>
    );
};

export default AdminLayout;
